import jwtDecode from 'jwt-decode';
import { ApiFactory } from './Api';

const api = new ApiFactory();

// Helper function to get customer ID from token
function getCustomerId() {
    const targetUserId = localStorage.getItem('target_user_id');
    if (targetUserId) {
        return targetUserId;
    }

    const token = localStorage.getItem('token');
    if (!token) return null;

    try {
        const decoded = jwtDecode(token);
        return decoded?.id || null;
    } catch (err) {
        console.warn('Invalid token:', err);
        return null;
    }
}

// Fetch customer info
async function fetchCustomerInfo() {
    try {
        const customerId = getCustomerId();
        if (!customerId) {
            console.warn('Skipping fetchCustomerInfo: no customer ID in token');
            return null;
        }
        const response = await api.get(`/customers/${customerId}`);
        return response.data;
    } catch (error) {
        console.error('Failed to fetch customer info:', error);
        throw error;
    }
}

// Edit customer profile
async function editProfile(request) {
    try {
        const customerId = getCustomerId();
        const response = await api.put(`/customers/${customerId}/edit_profile`, request);
        return response.data;
    } catch (error) {
        console.error('Failed to edit profile:', error);
        throw error;
    }
}

// Send OTP to change email
async function sendOtpToChangeEmail(request) {
    try {
        const customerId = getCustomerId();
        const response = await api.post(`/customers/${customerId}/send_otp_to_change_email`, request);
        return response.data;
    } catch (error) {
        console.error('Failed to send OTP to change email:', error);
        throw error;
    }
}

async function sendOtpToChangeEmailElectric(request) {
    try {
        const customerId = getCustomerId();
        const response = await api.post(`/customers/${customerId}/electric/send_otp_to_change_email`, request);
        return response.data;
    } catch (error) {
        console.error('Failed to send OTP to change email:', error);
        throw error;
    }
}

// Verify OTP to change email
async function verifyOtpChangeEmail(request) {
    try {
        const customerId = getCustomerId();
        const response = await api.post(`/customers/${customerId}/verify_otp_change_email`, request);
        return response.data;
    } catch (error) {
        console.error('Failed to verify OTP for email change:', error);
        throw error;
    }
}

// Send OTP to change phone
async function sendOtpToChangePhone(request) {
    try {
        const customerId = getCustomerId();
        const response = await api.post(`/customers/${customerId}/send_otp_to_change_phone`, request);
        return response.data;
    } catch (error) {
        console.error('Failed to send OTP to change phone:', error);
        throw error;
    }
}

// Verify OTP to change phone
async function verifyOtpChangePhone(request) {
    try {
        const customerId = getCustomerId();
        const response = await api.post(`/customers/${customerId}/verify_otp_change_phone`, request);
        return response.data;
    } catch (error) {
        console.error('Failed to verify OTP for phone change:', error);
        throw error;
    }
}

async function getCustomerCards() {
    try {
        const customerId = getCustomerId();
        const response = await api.get(`/customers/${customerId}/cards`);
        return response.data;
    } catch (error) {
        console.error('Failed to get customer cards:', error);
        throw error;
    }
}

async function changeDefaultCard(cardId) {
    try {
        const customerId = getCustomerId();
        const response = await api.patch(`/customers/${customerId}/cards/${cardId}/default`);
        return response.data;
    } catch (error) {
        console.error('Failed to get customer cards:', error);
        throw error;
    }
}

async function updateEmailSetting(request) {
    try {
        const customerId = getCustomerId();
        const response = await api.put(`/customers/${customerId}/email_setting`, request);
        return response.data;
    } catch (error) {
        console.error('Failed to edit profile:', error);
        throw error;
    }
}

async function updateMemberInfo(request) {
    try {
        const customerId = getCustomerId();
        const response = await api.put(`/customers/${customerId}/update_member_info`, request);
        return response.data;
    } catch (error) {
        console.error('Failed to edit profile:', error);
        throw error;
    }
}

async function updateContractEmail(request) {
    try {
        const { contractId, email } = request || {};
        const response = await api.put(`/electric/contracts/${contractId}/update_contract_email`, { email });
        return response.data;
    } catch (error) {
        console.error('Failed to update contract email:', error);
        throw error;
    }
}

async function sendOtpToChangeDrNotificationEmail(request) {
    try {
        const customerId = getCustomerId();
        const response = await api.post(`/customers/${customerId}/electric/send_otp_to_change_dr_notification_email`, request);
        return response.data;
    } catch (error) {
        console.error('Failed to send OTP to change DR notification email:', error);
        throw error;
    }
}

async function verifyOtpChangeDrNotificationEmail(request) {
    try {
        const customerId = getCustomerId();
        const response = await api.post(`/customers/${customerId}/electric/verify_otp_change_dr_notification_email`, request);
        return response.data;
    } catch (error) {
        console.error('Failed to verify OTP for DR notification email change:', error);
        throw error;
    }
}
/** POST /electric/dairiten/documents - body: { codes: ["code1", "code2", ...] } */
async function getDairitenDocuments(codes) {
    try {
        const uniqueCodes = Array.from(new Set((codes ?? []).map((code) => String(code).trim()))).filter(Boolean);

        if (uniqueCodes.length === 0) {
            return [];
        }

        const response = await api.post('/electric/dairiten/documents', { codes: uniqueCodes });
        return response.data ?? response ?? [];
    } catch (error) {
        console.error('Failed to fetch dairiten documents:', error);
        throw error;
    }
}

export {
    fetchCustomerInfo,
    editProfile,
    sendOtpToChangeEmail,
    sendOtpToChangeEmailElectric,
    verifyOtpChangeEmail,
    sendOtpToChangePhone,
    verifyOtpChangePhone,
    getCustomerCards,
    changeDefaultCard,
    updateEmailSetting,
    updateMemberInfo,
    updateContractEmail,
    sendOtpToChangeDrNotificationEmail,
    verifyOtpChangeDrNotificationEmail,
    getDairitenDocuments,
};
